import { useMemo, useState } from 'react';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import type { IncidentDetail, IncidentOptions } from '@isg/shared-types';
import { KILCIK_KATEGORILERI } from '@isg/validation';
import { useOlayEylem, useOlaySatirSil } from '../api/queries';
import { OLAY_ARASTIRMA_YONTEMI_ETIKET, SAGLIK_SINIRI_SERHI } from '../i18n/tr';
import { ANLAM } from '../theme';
import { Rozet, SolukSatir, SozlukSecici, type SecilebilirKalem } from '../sozlesme';

/**
 * SEBEP AĞACI DOLDURMA YÜZEYLERİ (F4·B2 — O1 · O2 · O4).
 *
 * > **Turun tek cümlesi B1'inkinin yüzey karşılığıdır: TEK SEBEP AĞACI, ÜÇ
 * > DOLDURMA BİÇİMİ.** B1 bunu veri tarafında kanıtladı (aynı
 * > `addIncidentCause` gövdesi üç şekli de kurdu); bu dosya aynı cümleyi
 * > ekranda kurar — **üç yüzey, TEK mutasyon** (`useOlayEylem`, tek "causes"
 * > çağrısı). Yönteme özel uç YOK, yönteme özel tablo YOK. Kod denetimi bunu
 * > ölçer: `olay-yontem.spec.ts` bu dosyada yazma çağrısını SAYAR.
 *
 * ⚠️ **DÖRDÜNCÜ YÖNTEM (neden ağacı · bariyer analizi) BU DOSYAYA BİR
 * BİLEŞENDİR** — şemaya, uca, tabloya dokunmaz. O1'in asıl değeri budur.
 *
 * ── YÖNTEM DEĞİŞTİRİLİRSE AĞAÇ NE OLUR? (bu turun kararı) ────────────────
 *
 * **AĞAÇ AYNEN KORUNUR. Silme YOK, dönüştürme de YOK — yalnız YÜZEY
 * değişir.** Üç gerekçe:
 *
 *  1. **Veri yapısı yöntemden bağımsızdır** (O1). Bir yöntem değişikliği
 *     kurulmuş düğümlerin hiçbirini geçersiz kılmaz; kayıt *"neyin sebep
 *     olduğunu"* söyler, *"hangi kağıda yazıldığını"* değil.
 *  2. **Yöntem bir KURAL değil, bir ARAÇTIR** (O4 — bu yüzden yapılandırma
 *     katmanı açılmadı). Aleti değiştirmek bulguyu silmez.
 *  3. **Dönüştürme, geçmiş kaydın anlamını değiştirirdi** — B1'in *"eski
 *     kalem yeninin adıyla yeniden yazılmaz"* dersinin aynısı. Bir 5 Neden
 *     zincirini kılçığa "dağıtmak", uzmanın kurmadığı bir hiyerarşiyi ona
 *     mal etmek olurdu.
 *
 * ⚠️ **Bunun bedeli görünür kılınır, gizlenmez:** yeni yüzey kendi
 * şekline oturmayan düğümleri çizemez; o zaman **sayıyı söyleyen bir bilgi
 * satırı** basar ve okuyucuyu değişmez tek ağaç görünümüne yollar (detayın
 * *"Sebep ağacı"* kartı). *Bir yüzeyin çizemediği düğüm, yok sayılan düğüm
 * değildir.*
 *
 * ── İŞARETLEME KURALI (ERD §12/39-iii · §2.40) ───────────────────────────
 *
 * **İşaretli düğüm KALEM ister.** Üç yüzeyde de kök sebep kutusu, sözlükten
 * bir kalem seçilmediği sürece **kapalıdır ve sebebi yanında yazar** —
 * basılınca hata veren bir kontrol değil. Kural sunucuda da koşar (DB CHECK
 * + servis); ekran onu yalnız **önden söyler**.
 */

// ── Ortak yardımcılar ───────────────────────────────────────────────────────

type Sebep = IncidentDetail['sebepler'][number];

/** `ustSebepId` → çocuklar. Ağaç SUNUCUDAN DÜZ LİSTE gelir (B1 kararı). */
function cocukHaritasi(sebepler: readonly Sebep[]): Map<string | null, Sebep[]> {
  const harita = new Map<string | null, Sebep[]>();
  for (const satir of sebepler) {
    harita.set(satir.ustSebepId, [...(harita.get(satir.ustSebepId) ?? []), satir]);
  }
  return harita;
}

function dugumEtiketi(satir: Sebep): string {
  return satir.sebepAd ?? satir.metin ?? '—';
}

/** Sözlük seçicinin beslendiği liste — kural alanları SUNUCUDAN (madde 37). */
function sebepKalemleri(options: IncidentOptions, katman?: 1 | 2): SecilebilirKalem[] {
  return options.kokSebepler
    .filter((s) => katman === undefined || s.katman === katman)
    .map((s) => ({
      id: s.id,
      ad: s.ad,
      katmanAd: s.katman === 1 ? 'Doğrudan sebepler' : 'Sistemsel sebepler',
      kategoriAd: s.grupAd ?? 'Sınıflandırılmamış',
      kategoriSira: s.grupSira,
      serh: s.saglikSiniri ? SAGLIK_SINIRI_SERHI : null,
    }));
}

/**
 * DÜĞÜM EKLEME — ÜÇ YÜZEYİN ORTAK GÖVDESİ (tek mutasyon, tek gövde şekli).
 * Yüzeyler yalnız `ustSebepId` / `kademe` / hangi alanların görüneceğini
 * değiştirir; **gönderilen istek aynıdır.**
 */
interface EklemeGovdesi {
  sebepRefId: string | null;
  kokSebepMi: boolean;
  ustSebepId: string | null;
  metin?: string;
  serbestMetin?: string;
  kademe?: 1 | 2;
}

function useDugumEkle(olayId: string): {
  ekle: (govde: EklemeGovdesi, bitince: (taze: IncidentDetail) => void) => void;
  bekliyor: boolean;
  hata: string | null;
} {
  const eylem = useOlayEylem();
  return {
    bekliyor: eylem.isPending,
    hata: eylem.isError ? eylem.error.message : null,
    // Uç TAZE DETAY döner; kılçık kökü gibi iki adımlı yazmalar bunu okur —
    // ikinci adım için yeniden sorgu atılmaz.
    ekle: (govde, bitince) =>
      eylem.mutate({ id: olayId, eylem: 'causes', govde: { ...govde } }, { onSuccess: bitince }),
  };
}

/** Yüzeyin çizemediği düğümleri SAYAR ve nereye bakılacağını söyler. */
function KapsamDisiNotu({ sayi }: { sayi: number }): React.JSX.Element {
  if (sayi === 0) return <></>;
  return (
    <Alert color={ANLAM.bilgi} data-testid="olay-yontem-kapsam-disi">
      Ağaçta bu yöntemin doldurma şekline oturmayan {sayi} düğüm var — hiçbiri silinmedi; hepsi
      aşağıdaki “Sebep ağacı” kartında görünür. Yöntem değiştirmek kurulmuş ağacı silmez.
    </Alert>
  );
}

/**
 * Kök sebep kutusu — üç yüzeyde de AYNI kural, tek bileşen: kalem yoksa
 * kapalı + görünür sebep (§2.40, seçenek 2: çalışamayacak kontrol görünmez
 * ya da salt okunurdur).
 */
function KokIsareti({
  testId,
  kalemSecili,
  checked,
  onChange,
}: {
  testId: string;
  kalemSecili: boolean;
  checked: boolean;
  onChange: (deger: boolean) => void;
}): React.JSX.Element {
  return (
    <Stack gap={2}>
      <Checkbox
        label="Kök sebep işareti"
        data-testid={testId}
        disabled={!kalemSecili}
        checked={checked && kalemSecili}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
      {!kalemSecili && (
        <Text size="xs" c="dimmed" data-testid={`${testId}-sebep`}>
          İşaretlemek için sözlükten bir kalem seçin — rapor işaretli kalemleri sayar.
        </Text>
      )}
    </Stack>
  );
}

function HataSatiri({ hata }: { hata: string | null }): React.JSX.Element {
  if (hata === null) return <></>;
  return (
    <Text size="sm" c={ANLAM.engel} role="alert" data-testid="olay-sebep-hata">
      {hata}
    </Text>
  );
}

// ── Yöntem seçimi (O4 — uzmanda, olay bazında) ──────────────────────────────

export function YontemSecici({
  olay,
  yazabilir,
}: {
  olay: IncidentDetail;
  yazabilir: boolean;
}): React.JSX.Element {
  const eylem = useOlayEylem();
  return (
    <Group align="end" wrap="wrap">
      <Select
        label="Araştırma yöntemi (olay bazında — uzman seçer)"
        data-testid="olay-yontem"
        clearable
        disabled={!yazabilir}
        data={Object.entries(OLAY_ARASTIRMA_YONTEMI_ETIKET).map(([deger, ad]) => ({
          value: deger,
          label: ad,
        }))}
        value={olay.arastirmaYontemi}
        onChange={(deger) =>
          eylem.mutate({ id: olay.id, eylem: 'arastirma-yontemi', govde: { yontem: deger } })
        }
        w={280}
      />
      <Text size="xs" c="dimmed" maw={460}>
        Üç yöntem <b>aynı</b> sebep ağacını farklı doldurur. Yöntem değiştirmek kurulmuş ağacı
        silmez; yalnız doldurma yüzeyi değişir.
      </Text>
    </Group>
  );
}

// ── Yüzey dağıtıcısı ────────────────────────────────────────────────────────

export function SebepYuzeyi({
  olay,
  options,
}: {
  olay: IncidentDetail;
  options: IncidentOptions;
}): React.JSX.Element {
  if (olay.arastirmaYontemi === 'five_whys')
    return <BesNedenYuzeyi olay={olay} options={options} />;
  if (olay.arastirmaYontemi === 'fishbone') return <KilcikYuzeyi olay={olay} options={options} />;
  if (olay.arastirmaYontemi === 'cause_table') {
    return <SebepTablosuYuzeyi olay={olay} options={options} />;
  }
  return <GenelYuzey olay={olay} options={options} />;
}

// ── 5 NEDEN — tek kollu DERİN zincir ────────────────────────────────────────

/**
 * *"Neden?"* zinciri: her düğüm bir öncekinin cevabıdır. Düğümler
 * **çoğunlukla serbest metindir** (`sebep_ref_id` NULL) — bu yüzden zincirin
 * ortasındaki bir adım **işaretlenemez** ve ekran bunu önden söyler.
 *
 * Zincirin SONU sözlükten seçilebilir: 5 Neden zaten kök sebepte biter ve
 * işaret ancak orada anlamlıdır (rapor işaretli KALEMLERİ sayar).
 */
function BesNedenYuzeyi({
  olay,
  options,
}: {
  olay: IncidentDetail;
  options: IncidentOptions;
}): React.JSX.Element {
  const { ekle, bekliyor, hata } = useDugumEkle(olay.id);
  const [metin, setMetin] = useState('');
  const [sozluktenMi, setSozluktenMi] = useState(false);
  const [sebepId, setSebepId] = useState<string | null>(null);
  const [serbestMetin, setSerbestMetin] = useState('');
  const [kok, setKok] = useState(true);

  // ZİNCİR: tek kökten başlayıp TEK çocuğu izler. Dallanma varsa zincir
  // biter — kalan düğümler kapsam dışı sayılır (silinmez, sayılır).
  const { zincir, kapsamDisi } = useMemo(() => {
    const cocuklar = cocukHaritasi(olay.sebepler);
    const kokler = cocuklar.get(null) ?? [];
    const yol: Sebep[] = [];
    let simdiki: Sebep | undefined = kokler.length === 1 ? kokler[0] : undefined;
    while (simdiki !== undefined) {
      yol.push(simdiki);
      const alt: Sebep[] = cocuklar.get(simdiki.id) ?? [];
      simdiki = alt.length === 1 ? alt[0] : undefined;
    }
    return { zincir: yol, kapsamDisi: olay.sebepler.length - yol.length };
  }, [olay.sebepler]);

  const secili = options.kokSebepler.find((s) => s.id === sebepId) ?? null;
  const digerEksik = (secili?.digerMi ?? false) && serbestMetin.trim() === '';
  const eklenebilir = sozluktenMi
    ? sebepId !== null && !digerEksik && !bekliyor
    : metin.trim() !== '' && !bekliyor;
  const sonHalka = zincir.length > 0 ? (zincir[zincir.length - 1]?.id ?? null) : null;

  return (
    <Stack gap="xs" data-testid="olay-yuzey-five-whys">
      <KapsamDisiNotu sayi={kapsamDisi} />
      <Text size="sm" c="dimmed">
        Her adım bir öncekinin cevabıdır. Zincir kök sebepte biter; işaretlemek için son adımı
        sözlükten seçin.
      </Text>
      <Stack gap={4} data-testid="olay-neden-zinciri">
        {zincir.map((satir, sira) => (
          <Group key={satir.id} gap="xs" wrap="nowrap" data-testid={`olay-neden-${sira + 1}`}>
            <Text size="sm" c="dimmed" w={72}>
              {sira === 0 ? 'Olay →' : `${sira}. Neden?`}
            </Text>
            <Text size="sm">{dugumEtiketi(satir)}</Text>
            {satir.kokSebepMi && <Rozet anlam="engel">Kök sebep</Rozet>}
          </Group>
        ))}
      </Stack>
      <Group align="end" wrap="wrap">
        <Text size="sm" c="dimmed" w={72}>
          {zincir.length === 0 ? 'Olay →' : `${zincir.length}. Neden?`}
        </Text>
        {sozluktenMi ? (
          <Box w={340}>
            <SozlukSecici
              label="Son adım (sözlükten — kök sebep)"
              testId="olay-neden-kalem"
              kalemler={sebepKalemleri(options)}
              value={sebepId}
              onChange={setSebepId}
            />
          </Box>
        ) : (
          <TextInput
            label="Cevap"
            data-testid="olay-neden-metin"
            value={metin}
            onChange={(event) => setMetin(event.currentTarget.value)}
            w={340}
          />
        )}
        {sozluktenMi && (secili?.digerMi ?? false) && (
          <TextInput
            label='Serbest metin ("Diğer" kaleminde zorunlu)'
            data-testid="olay-neden-diger-metin"
            value={serbestMetin}
            onChange={(event) => setSerbestMetin(event.currentTarget.value)}
            w={260}
          />
        )}
        <Checkbox
          label="Sözlükten seç"
          data-testid="olay-neden-sozlukten"
          checked={sozluktenMi}
          onChange={(event) => {
            setSozluktenMi(event.currentTarget.checked);
            if (!event.currentTarget.checked) setSebepId(null);
          }}
        />
        <KokIsareti
          testId="olay-neden-kok"
          kalemSecili={sozluktenMi && sebepId !== null}
          checked={kok}
          onChange={setKok}
        />
        <Button
          data-testid="olay-neden-ekle"
          disabled={!eklenebilir}
          loading={bekliyor}
          onClick={() =>
            ekle(
              {
                sebepRefId: sozluktenMi ? sebepId : null,
                kokSebepMi: sozluktenMi && sebepId !== null && kok,
                ustSebepId: sonHalka,
                ...(sozluktenMi ? {} : { metin: metin.trim() }),
                ...(sozluktenMi && serbestMetin.trim() !== ''
                  ? { serbestMetin: serbestMetin.trim() }
                  : {}),
              },
              () => {
                setMetin('');
                setSebepId(null);
                setSerbestMetin('');
              },
            )
          }
        >
          Ekle
        </Button>
      </Group>
      <HataSatiri hata={hata} />
    </Stack>
  );
}

// ── BALIK KILÇIĞI — iki katmanlı GENİŞ ağaç ─────────────────────────────────

/**
 * Kılçık kökleri **serbest metin düğümleridir** (`KILCIK_KATEGORILERI` —
 * kural katmanında, sözlükte DEĞİL: bir başlık bir sebep değildir). Bu
 * yüzden kökler işaretlenemez; işaret alt düğümlerdedir.
 *
 * ⚠️ **KÖK LAZIM OLUNCA DOĞAR:** boş kılçıklar için önden altı düğüm
 * yazılmaz — kullanmadığı beş başlığı ağacında bulan uzman, onları silmeye
 * çalışır ve silme alt dalı da götürür.
 */
function KilcikYuzeyi({
  olay,
  options,
}: {
  olay: IncidentDetail;
  options: IncidentOptions;
}): React.JSX.Element {
  const { ekle, bekliyor, hata } = useDugumEkle(olay.id);
  const [acikKategori, setAcikKategori] = useState<string | null>(null);
  const [sebepId, setSebepId] = useState<string | null>(null);
  const [metin, setMetin] = useState('');
  const [serbestMetin, setSerbestMetin] = useState('');
  const [kok, setKok] = useState(true);

  const cocuklar = useMemo(() => cocukHaritasi(olay.sebepler), [olay.sebepler]);
  const kokler = cocuklar.get(null) ?? [];
  const kilcikDugumu = (kategori: string): Sebep | undefined =>
    kokler.find((satir) => satir.sebepRefId === null && satir.metin === kategori);
  const kilcikKokleri = KILCIK_KATEGORILERI.map((k) => kilcikDugumu(k)).filter(
    (s): s is Sebep => s !== undefined,
  );
  const kapsanan = new Set<string>();
  for (const kokDugum of kilcikKokleri) {
    kapsanan.add(kokDugum.id);
    for (const alt of cocuklar.get(kokDugum.id) ?? []) kapsanan.add(alt.id);
  }
  const kapsamDisi = olay.sebepler.length - kapsanan.size;

  const secili = options.kokSebepler.find((s) => s.id === sebepId) ?? null;
  const digerEksik = (secili?.digerMi ?? false) && serbestMetin.trim() === '';
  const eklenebilir = (sebepId !== null || metin.trim() !== '') && !digerEksik && !bekliyor;

  /**
   * Kılçık kökü yoksa ÖNCE onu açar, sonra alt düğümü ona asar — **tek
   * basış.** İkinci yazmanın üst düğümü, birinci yazmanın döndürdüğü TAZE
   * DETAYDAN okunur (kökler arasında `metin` benzersizdir); ikinci bir
   * sorgu atılmaz.
   */
  const kilcigaEkle = (kategori: string): void => {
    const altDugum = (ustSebepId: string): void =>
      ekle(
        {
          sebepRefId: sebepId,
          kokSebepMi: sebepId !== null && kok,
          ustSebepId,
          ...(metin.trim() !== '' ? { metin: metin.trim() } : {}),
          ...(serbestMetin.trim() !== '' ? { serbestMetin: serbestMetin.trim() } : {}),
        },
        () => {
          setSebepId(null);
          setMetin('');
          setSerbestMetin('');
        },
      );
    const mevcut = kilcikDugumu(kategori);
    if (mevcut) {
      altDugum(mevcut.id);
      return;
    }
    ekle(
      { sebepRefId: null, kokSebepMi: false, ustSebepId: null, metin: kategori, kademe: 1 },
      (taze) => {
        const yeniKok = taze.sebepler.find(
          (satir) =>
            satir.ustSebepId === null && satir.sebepRefId === null && satir.metin === kategori,
        );
        if (yeniKok) altDugum(yeniKok.id);
      },
    );
  };

  return (
    <Stack gap="xs" data-testid="olay-yuzey-fishbone">
      <KapsamDisiNotu sayi={kapsamDisi} />
      <Text size="sm" c="dimmed">
        Altı kılçık, her biri altında sebepler. Başlıklar sebep değildir — işaret alt düğümlerde
        olur.
      </Text>
      {/* ⚠️ Test kimlikleri SIRA numarasıyla: kategori adları boşluk ve eğik
          çizgi taşır ("Makine / ekipman") ve seçicide kaçış gerektirirdi. */}
      {KILCIK_KATEGORILERI.map((kategori, sira) => {
        const kokDugum = kilcikDugumu(kategori);
        const altlar = kokDugum ? (cocuklar.get(kokDugum.id) ?? []) : [];
        const acik = acikKategori === kategori;
        return (
          <Card key={kategori} withBorder padding="xs" data-testid={`olay-kilcik-${sira}`}>
            <Group justify="space-between">
              <Text fw={600} size="sm">
                {kategori}
              </Text>
              <Button
                size="compact-sm"
                variant="light"
                data-testid={`olay-kilcik-ac-${sira}`}
                onClick={() => setAcikKategori(acik ? null : kategori)}
              >
                {acik ? 'Kapat' : 'Sebep ekle'}
              </Button>
            </Group>
            <Stack gap={2} mt={altlar.length > 0 ? 'xs' : 0}>
              {altlar.map((satir) => (
                <Group key={satir.id} gap="xs">
                  <Text size="sm">{dugumEtiketi(satir)}</Text>
                  {satir.kokSebepMi && <Rozet anlam="engel">Kök sebep</Rozet>}
                </Group>
              ))}
            </Stack>
            {acik && (
              <Group align="end" mt="xs" wrap="wrap">
                <Box w={320}>
                  <SozlukSecici
                    label="Sebep kalemi (sözlükten)"
                    testId={`olay-kilcik-kalem-${sira}`}
                    kalemler={sebepKalemleri(options)}
                    value={sebepId}
                    onChange={setSebepId}
                  />
                </Box>
                <TextInput
                  label="Serbest açıklama (kalem yoksa zorunlu)"
                  data-testid={`olay-kilcik-metin-${sira}`}
                  value={metin}
                  onChange={(event) => setMetin(event.currentTarget.value)}
                  w={280}
                />
                {(secili?.digerMi ?? false) && (
                  <TextInput
                    label='Serbest metin ("Diğer" kaleminde zorunlu)'
                    data-testid={`olay-kilcik-diger-${sira}`}
                    value={serbestMetin}
                    onChange={(event) => setSerbestMetin(event.currentTarget.value)}
                    w={240}
                  />
                )}
                <KokIsareti
                  testId={`olay-kilcik-kok-${sira}`}
                  kalemSecili={sebepId !== null}
                  checked={kok}
                  onChange={setKok}
                />
                <Button
                  data-testid={`olay-kilcik-ekle-${sira}`}
                  disabled={!eklenebilir}
                  loading={bekliyor}
                  onClick={() => kilcigaEkle(kategori)}
                >
                  Ekle
                </Button>
              </Group>
            )}
          </Card>
        );
      })}
      <HataSatiri hata={hata} />
    </Stack>
  );
}

// ── SEBEP TABLOSU — iki katman, düğümler SÖZLÜKTEN ──────────────────────────

/**
 * İki basamak: önce **doğrudan** sebepler (taksonomi 1. katman — D1-D8),
 * sonra her birinin altına **sistemsel** sebepler (K1-K6 · S1-S11).
 *
 * ⚠️ **BU YÜZEYDE SERBEST METİN DÜĞÜMÜ YOKTUR** — tablonun tamamı sözlükten
 * doldurulur; serbest metin yalnız *"Diğer"* kaleminin zorunlu açıklamasıdır
 * (§19/13). Konsolidasyonun *"en sık kök sebepler"* tablosu bu yüzeyden
 * doğar.
 */
function SebepTablosuYuzeyi({
  olay,
  options,
}: {
  olay: IncidentDetail;
  options: IncidentOptions;
}): React.JSX.Element {
  const { ekle, bekliyor, hata } = useDugumEkle(olay.id);
  const [dogrudanId, setDogrudanId] = useState<string | null>(null);
  const [dogrudanMetin, setDogrudanMetin] = useState('');
  const [ustId, setUstId] = useState<string | null>(null);
  const [sistemselId, setSistemselId] = useState<string | null>(null);
  const [sistemselMetin, setSistemselMetin] = useState('');
  const [kok, setKok] = useState(true);

  const cocuklar = useMemo(() => cocukHaritasi(olay.sebepler), [olay.sebepler]);
  const dogrudanlar = (cocuklar.get(null) ?? []).filter((s) => s.sebepRefId !== null);
  const kapsanan = new Set<string>();
  for (const satir of dogrudanlar) {
    kapsanan.add(satir.id);
    for (const alt of cocuklar.get(satir.id) ?? []) kapsanan.add(alt.id);
  }
  const kapsamDisi = olay.sebepler.length - kapsanan.size;

  const kalem = (id: string | null) => options.kokSebepler.find((s) => s.id === id) ?? null;
  const dogrudanDigerEksik = (kalem(dogrudanId)?.digerMi ?? false) && dogrudanMetin.trim() === '';
  const sistemselDigerEksik =
    (kalem(sistemselId)?.digerMi ?? false) && sistemselMetin.trim() === '';

  return (
    <Stack gap="sm" data-testid="olay-yuzey-cause-table">
      <KapsamDisiNotu sayi={kapsamDisi} />
      <Text size="sm" c="dimmed">
        Önce doğrudan sebepler, sonra her birinin altına sistemsel sebepler. Bu yüzeyde düğümler
        yalnız sözlükten seçilir.
      </Text>

      <Box>
        <Title order={6}>1 — Doğrudan sebepler</Title>
        <Stack gap={2} mt="xs">
          {dogrudanlar.map((satir) => (
            <Box key={satir.id}>
              <Group gap="xs">
                <Text size="sm">{dugumEtiketi(satir)}</Text>
                {satir.kokSebepMi && <Rozet anlam="engel">Kök sebep</Rozet>}
              </Group>
              {(cocuklar.get(satir.id) ?? []).map((alt) => (
                <Group key={alt.id} gap="xs" pl="lg">
                  <Text size="sm" c="dimmed">
                    ↳ {dugumEtiketi(alt)}
                  </Text>
                  {alt.kokSebepMi && <Rozet anlam="engel">Kök sebep</Rozet>}
                </Group>
              ))}
            </Box>
          ))}
        </Stack>
        <Group align="end" mt="xs" wrap="wrap">
          <Box w={340}>
            <SozlukSecici
              label="Doğrudan sebep (1. katman)"
              testId="olay-tablo-dogrudan"
              kalemler={sebepKalemleri(options, 1)}
              value={dogrudanId}
              onChange={setDogrudanId}
            />
          </Box>
          {(kalem(dogrudanId)?.digerMi ?? false) && (
            <TextInput
              label='Serbest metin ("Diğer" kaleminde zorunlu)'
              data-testid="olay-tablo-dogrudan-diger"
              value={dogrudanMetin}
              onChange={(event) => setDogrudanMetin(event.currentTarget.value)}
              w={260}
            />
          )}
          <Button
            data-testid="olay-tablo-dogrudan-ekle"
            disabled={dogrudanId === null || dogrudanDigerEksik || bekliyor}
            loading={bekliyor}
            onClick={() =>
              ekle(
                {
                  sebepRefId: dogrudanId,
                  kokSebepMi: false,
                  ustSebepId: null,
                  kademe: 1,
                  ...(dogrudanMetin.trim() !== '' ? { serbestMetin: dogrudanMetin.trim() } : {}),
                },
                () => {
                  setDogrudanId(null);
                  setDogrudanMetin('');
                },
              )
            }
          >
            Ekle
          </Button>
        </Group>
      </Box>

      <Box>
        <Title order={6}>2 — Sistemsel sebepler</Title>
        <Group align="end" mt="xs" wrap="wrap">
          <Select
            label="Hangi doğrudan sebebin altına?"
            data-testid="olay-tablo-ust"
            searchable
            clearable
            data={dogrudanlar.map((satir) => ({ value: satir.id, label: dugumEtiketi(satir) }))}
            value={ustId}
            onChange={setUstId}
            w={280}
          />
          <Box w={340}>
            <SozlukSecici
              label="Sistemsel sebep (2. katman)"
              testId="olay-tablo-sistemsel"
              kalemler={sebepKalemleri(options, 2)}
              value={sistemselId}
              onChange={setSistemselId}
            />
          </Box>
          {(kalem(sistemselId)?.digerMi ?? false) && (
            <TextInput
              label='Serbest metin ("Diğer" kaleminde zorunlu)'
              data-testid="olay-tablo-sistemsel-diger"
              value={sistemselMetin}
              onChange={(event) => setSistemselMetin(event.currentTarget.value)}
              w={260}
            />
          )}
          <KokIsareti
            testId="olay-tablo-kok"
            kalemSecili={sistemselId !== null}
            checked={kok}
            onChange={setKok}
          />
          <Button
            data-testid="olay-tablo-sistemsel-ekle"
            disabled={ustId === null || sistemselId === null || sistemselDigerEksik || bekliyor}
            loading={bekliyor}
            onClick={() =>
              ekle(
                {
                  sebepRefId: sistemselId,
                  kokSebepMi: kok,
                  ustSebepId: ustId,
                  kademe: 2,
                  ...(sistemselMetin.trim() !== '' ? { serbestMetin: sistemselMetin.trim() } : {}),
                },
                () => {
                  setSistemselId(null);
                  setSistemselMetin('');
                },
              )
            }
          >
            Ekle
          </Button>
        </Group>
      </Box>
      <HataSatiri hata={hata} />
    </Stack>
  );
}

// ── Yöntemsiz genel yüzey (B1'in formu — yöntem seçilmemişse) ───────────────

/**
 * Yöntem seçilmemiş olayın yüzeyi. **Kaldırılmadı:** yöntem bir araçtır ve
 * seçmek zorunlu değildir (O4 — yapılandırma katmanı yok); yöntemsiz kayıtta
 * ağaç yine kurulabilmelidir.
 */
function GenelYuzey({
  olay,
  options,
}: {
  olay: IncidentDetail;
  options: IncidentOptions;
}): React.JSX.Element {
  const { ekle, bekliyor, hata } = useDugumEkle(olay.id);
  const [sebepId, setSebepId] = useState<string | null>(null);
  const [ustSebepId, setUstSebepId] = useState<string | null>(null);
  const [dugumMetni, setDugumMetni] = useState('');
  const [serbestMetin, setSerbestMetin] = useState('');
  const [kok, setKok] = useState(true);

  const secili = options.kokSebepler.find((s) => s.id === sebepId) ?? null;
  const digerEksik = (secili?.digerMi ?? false) && serbestMetin.trim() === '';
  const eklenebilir = (sebepId !== null || dugumMetni.trim() !== '') && !digerEksik && !bekliyor;

  // GEZİNTİ ARTÇISI (30.08.2026) — P1-a'nın TAM UYGULAMASI. B2 soluk satırı
  // getirmişti ama kaydedilen satır bu tablodan KAYBOLUYORDU (yalnız aşağıdaki
  // "Sebep ağacı" kartına düşüyordu): kullanıcı girdiğini girdiği yerde
  // görmüyor, akış "seç → kaydet → nereye gitti?" oluyordu. P1-a'nın tarifi
  // (kronoloji emsali): KAYDEDİLEN SATIR ÜSTÜNE OTURUR, ALTINDA yeni soluk
  // satır hazır durur ve ODAK oradadır — art arda girişte düğme aranmaz.
  // Yazma yolu DEĞİŞMEDİ — tek mutasyon (useDugumEkle).
  const cocuklar = useMemo(() => cocukHaritasi(olay.sebepler), [olay.sebepler]);
  const kayitliSatirlar = useMemo(() => {
    const sonuc: { satir: Sebep; derinlik: number }[] = [];
    const yur = (ustId: string | null, derinlik: number): void => {
      for (const satir of cocuklar.get(ustId) ?? []) {
        sonuc.push({ satir, derinlik });
        yur(satir.id, derinlik + 1);
      }
    };
    yur(null, 0);
    return sonuc;
  }, [cocuklar]);
  const dokunuldu = sebepId !== null || dugumMetni.trim() !== '' || serbestMetin.trim() !== '';
  // P12 (madde 224) — kaydedilmemiş sebep satırı çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(dokunuldu ? 1 : 0, 'sebep satırı');
  return (
    <Stack gap="xs" data-testid="olay-yuzey-genel">
      <Text size="sm" c="dimmed">
        Yöntem seçilmedi — ağaç doğrudan doldurulabilir. Yöntem seçmek yalnız doldurma yüzeyini
        değiştirir.
      </Text>
      <Table>
        <Table.Tbody>
          {kayitliSatirlar.map(({ satir, derinlik }) => (
            <Table.Tr key={satir.id} data-testid="olay-sebep-satiri">
              <Table.Td>
                <Group gap="xs" wrap="wrap" pl={derinlik * 24}>
                  <Text size="sm">{dugumEtiketi(satir)}</Text>
                  {satir.kokSebepMi && <Rozet anlam="engel">Kök sebep</Rozet>}
                  {satir.grupAd && (
                    <Text size="xs" c="dimmed">
                      {satir.grupAd}
                    </Text>
                  )}
                  {satir.serbestMetin && (
                    <Text size="xs" c="dimmed">
                      {satir.serbestMetin}
                    </Text>
                  )}
                </Group>
              </Table.Td>
              {/* Boş hücre soluk satırın kaydet kolonuyla hizalanır; silme ve
                  DÖF eylemleri değişmez tek ağaç görünümünde yaşar (P2-a). */}
              <Table.Td />
            </Table.Tr>
          ))}
          <SolukSatir
            testId="olay-sebep-soluk-satir"
            dokunuldu={dokunuldu}
            kaydedilebilir={eklenebilir}
            kaydediliyor={bekliyor}
            kolonSayisi={1}
            onKaydet={() => {
              if (!eklenebilir) return;
              ekle(
                {
                  sebepRefId: sebepId,
                  kokSebepMi: sebepId !== null && kok,
                  ustSebepId,
                  ...(dugumMetni.trim() !== '' ? { metin: dugumMetni.trim() } : {}),
                  ...(serbestMetin.trim() !== '' ? { serbestMetin: serbestMetin.trim() } : {}),
                },
                () => {
                  setSebepId(null);
                  setDugumMetni('');
                  setSerbestMetin('');
                  // P1-a — ODAK ORADA: kaydedilen satır üste oturur, boşalan
                  // soluk satırın ilk denetimi (kalem seçici) odağı alır —
                  // art arda girişte kullanıcı düğme/alan aramaz.
                  document
                    .querySelector<HTMLButtonElement>('[data-testid="olay-sebep-kalem"]')
                    ?.focus();
                },
              );
            }}
          >
            <Table.Td>
              <Group align="end" gap="sm" wrap="wrap">
                <Box w={300}>
                  <SozlukSecici
                    label="Sebep kalemi (sözlükten)"
                    testId="olay-sebep-kalem"
                    kalemler={sebepKalemleri(options)}
                    value={sebepId}
                    onChange={setSebepId}
                  />
                </Box>
                <Select
                  label="Üst sebep (boş = doğrudan olaya bağlı)"
                  data-testid="olay-sebep-ust"
                  clearable
                  searchable
                  data={olay.sebepler.map((satir) => ({
                    value: satir.id,
                    label: dugumEtiketi(satir),
                  }))}
                  value={ustSebepId}
                  onChange={setUstSebepId}
                  w={250}
                />
                <TextInput
                  label="Düğüm metni (kalem seçilmediyse zorunlu)"
                  data-testid="olay-sebep-dugum-metni"
                  value={dugumMetni}
                  onChange={(event) => setDugumMetni(event.currentTarget.value)}
                  w={280}
                />
                <TextInput
                  label='Serbest metin ("Diğer" kaleminde zorunlu)'
                  data-testid="olay-sebep-metin"
                  value={serbestMetin}
                  onChange={(event) => setSerbestMetin(event.currentTarget.value)}
                  w={250}
                />
                <KokIsareti
                  testId="olay-sebep-kok"
                  kalemSecili={sebepId !== null}
                  checked={kok}
                  onChange={setKok}
                />
              </Group>
            </Table.Td>
          </SolukSatir>
        </Table.Tbody>
      </Table>
      <HataSatiri hata={hata} />
    </Stack>
  );
}

/**
 * DEĞİŞMEZ TEK AĞAÇ GÖRÜNÜMÜ — yöntemden BAĞIMSIZ. Üç yüzey de buraya yazar,
 * silme YALNIZ burada. ⚠️ **Silme alt dalı da götürür** (serviste) ve düğme
 * bunu söyler: bir zincirin ortasını sessizce silmek, kuyruğunu olayın
 * doğrudan sebebi hâline getirirdi.
 *
 * F4·B4 — "DÖF SEBEPLERE AÇILIR": `onDofAc` verildiyse her satır "DÖF aç"
 * eylemi taşır ve sebebe bağlı DÖF sayısı/durumu SATIRDA görünür (sayaçlar
 * SUNUCUDAN — ekran saymaz). Genel düğme araştırmada kalktı.
 */
export function SebepAgaci({
  olay,
  yazabilir,
  onDofAc,
}: {
  olay: IncidentDetail;
  yazabilir: boolean;
  onDofAc?: (satir: Sebep) => void;
}): React.JSX.Element {
  const sil = useOlaySatirSil();
  const cocuklar = cocukHaritasi(olay.sebepler);
  const ciz = (ustId: string | null, derinlik: number): React.JSX.Element[] =>
    (cocuklar.get(ustId) ?? []).flatMap((satir) => [
      <Group key={satir.id} gap="xs" wrap="nowrap" data-testid={`olay-sebep-${satir.id}`}>
        <Box style={{ paddingInlineStart: derinlik * 24, minWidth: 0, flex: 1 }}>
          <Group gap="xs" wrap="wrap">
            <Text size="sm">{dugumEtiketi(satir)}</Text>
            {satir.kokSebepMi && <Rozet anlam="engel">Kök sebep</Rozet>}
            {satir.grupAd && (
              <Text size="xs" c="dimmed">
                {satir.grupAd}
              </Text>
            )}
            {satir.dofToplam > 0 && (
              <Text size="xs" c="dimmed" data-testid={`olay-sebep-dof-sayac-${satir.id}`}>
                DÖF {satir.dofToplam - satir.dofAcik}/{satir.dofToplam} kapalı
              </Text>
            )}
          </Group>
          {satir.sebepAd && satir.metin && (
            <Text size="xs" c="dimmed">
              {satir.metin}
            </Text>
          )}
          {satir.serbestMetin && (
            <Text size="xs" c="dimmed">
              {satir.serbestMetin}
            </Text>
          )}
        </Box>
        {onDofAc && (
          <Button
            size="compact-xs"
            variant="light"
            data-testid={`olay-sebep-dof-ac-${satir.id}`}
            onClick={() => onDofAc(satir)}
          >
            DÖF aç
          </Button>
        )}
        {yazabilir && (
          <Button
            size="compact-xs"
            variant="subtle"
            color={ANLAM.engel}
            data-testid={`olay-sebep-kaldir-${satir.id}`}
            onClick={() => sil.mutate({ id: olay.id, liste: 'causes', satirId: satir.id })}
          >
            Kaldır
          </Button>
        )}
      </Group>,
      ...ciz(satir.id, derinlik + 1),
    ]);
  return (
    <Stack gap={4} mt="xs">
      {ciz(null, 0)}
    </Stack>
  );
}
